/**
 * @file 本だな (取り込んだ資料) のクライアント。
 *
 * @what: `GET /families/{familyId}/study/sources` の取得と、画面に出す日本語ラベル。
 *   資料は目次 (toc) と別冊の対応 (companion_of)、資料どうしの対応づけ (related) を持ち、
 *   ページで読むときの見出しと、問題から別冊 (解答例) や関連する資料の該当ページへ飛ぶ鍵になる。
 * @why: 取り込んだものを読む仕組みは汎用で、教材も入試問題集も同じ本だなに並ぶ。
 *   分けるのは役割 (kind) だけ (代表 2026-08-22)。
 * @failure: ApiError を伝播。空配列 = まだ何も取り込んでいない、は正常系。
 */

import { apiFetch } from "@/shared/api/client";

export type StudySourceKind = "material" | "reference" | "culture";
export type StudySourceOwner = "personal" | "company";

/** 目次の 1 行 (見出しと、その見出しが始まる紙のページ番号)。終了ページは次の行で決まる。 */
export interface StudySourceTocEntry {
  title: string;
  page: number;
}

export interface StudySource {
  id: string;
  title: string;
  kind: StudySourceKind;
  page_count?: number;
  owner: StudySourceOwner;
  note?: string;
  /** 目次 (ページ順)。無ければ省略。 */
  toc?: StudySourceTocEntry[];
  /** この資料が別冊として付く本体の資料 id (例: 解答例 → 問題編)。単独の資料では省略。 */
  companion_of?: string;
  /** 資料どうしの対応づけ (この資料の問 → 相手の資料のページ)。無ければ省略。 */
  related?: StudySourceRelation[];
  /** ページの対応 (api/sourcePageLinks.ts) が結ぶ相手の資料 id。無ければ省略される。 */
  page_link_targets?: string[];
  /** さくいん (api/sourceIndex.ts) の語の数。無ければ省略 (= さくいん無し)。 */
  index_count?: number;
  /** 対応する作品 (api/studyWorks.ts) の id。付いていれば「問題ごとの解説」の導線を出す。無ければ省略である。 */
  work_id?: string;
  /**
   * 市販の本の ISBN-13。参考書ルートの本と同じ本かを突き合わせるために持ち、一致した本に
   * 「本だなにある」の印を出す。別冊 (companion_of を持つ資料) には入れない。無ければ省略される。
   */
  isbn?: string;
  /** リーダーの連番と印刷ページの差で、連番は印刷ページにこの値を足したものになる。0のときは省略される。 */
  reader_offset?: number;
  /** 綴じ方向で、見開きで左右に並べる順を決める。right は右開き (後のページが左)。左開きのときは省略される。 */
  binding?: "left" | "right";
  /** 本だなの「教養」の区画で並べる分野の id (model/shelfSubjects.ts の語彙)。無ければ省略される。 */
  subject?: string;
  /** 何の本か・どんなときに開くかの1〜2文である。本だなのカードには出さない (冗長になる。代表の判断、2026-09-17)。無ければ省略される。 */
  summary?: string;
  /**
   * 本だなのカードに出す表紙の縮小画像の Storage パス (CLI が先頭ページを幅300pxに縮めて上げる)。
   * 無ければ省略され、web は先頭ページの原寸画像に戻る (hooks/useSourceCover.ts)。
   */
  cover_path?: string;
  /** 表紙の縮小画像の MIME (image/jpeg)。cover_path があるときだけ来る。 */
  cover_content_type?: string;
  created_at: string;
  updated_at: string;
}

/**
 * 資料どうしの対応づけの 1 行。
 *
 * @what: この資料の問 (scan_items) の index[field] の値を、相手の資料 (source_id) の目次の
 *   見出しと索引 (api/sourceIndex.ts) の語に突き合わせる (model/sourceLinks.ts)。
 */
export interface StudySourceRelation {
  source_id: string;
  field: string;
}

/**
 * 区画の表示名を持つ。material = 選んでいる人の計画に入っている本、reference = 計画を立てるときに
 * 見るもの、culture = 計画に入っていない本を指す。
 *
 * @why: 本だなに並べる区画は、取り込んだときの役割 (kind) をそのまま出すのではなく、選んでいる人の
 *   計画から決め直す (model/shelfSections.ts)。同じ本でも、中学入試を目指す人にとっての大学入試の
 *   参考書は取り組む対象にならない (代表の判断、2026-09-11)。
 */
export const STUDY_SOURCE_KIND_LABELS: Record<StudySourceKind, string> = {
  material: "取り組むもの",
  reference: "計画のもと",
  culture: "教養",
};

export const STUDY_SOURCE_KIND_HELP: Record<StudySourceKind, string> = {
  material: "いまの計画でやると決まっている本",
  reference: "何をいつまでにやるか決めるときに見るもの",
  culture: "計画に入っていない、読みたいときに開く本",
};

/** 区画は 取り組むもの → 計画に使うもの → 読むだけのもの の順に並べる。 */
export const STUDY_SOURCE_KIND_ORDER: StudySourceKind[] = ["material", "reference", "culture"];

/** 画面をまたいで使い回す一覧の覚え (lib/sharedFetchCache.ts) の key である。 */
export function studySourcesCacheKey(familyId: string): string {
  return `study-sources:${familyId}`;
}

export async function fetchStudySources(
  familyId: string,
  signal?: AbortSignal,
): Promise<StudySource[]> {
  const res = await apiFetch<{ sources: StudySource[] }>(
    `/families/${encodeURIComponent(familyId)}/study/sources`,
    signal ? { signal } : {},
  );
  return res.sources;
}
